import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/images/KanpurLogo.png";

type SplashScreenProps = {
  duration?: number;
  navigateTo: string;
  loadingText?: string;
  showSpinner?: boolean;
  logoSize?: number;
  backgroundColor?: string;
};

const SplashScreen: React.FC<SplashScreenProps> = ({
  duration = 2500,
  navigateTo,
  loadingText = "Please wait, we're preparing your dashboard...",
  showSpinner = true,
  logoSize = 110,
  backgroundColor = "linear-gradient(135deg, #f9f6ff, #e0c9f5, #ffffff)"
}) => {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      // Wait for the fade-out animation to complete before navigating
      setTimeout(() => navigate(navigateTo), 500);
    }, duration - 500); // Start exit animation 500ms before the actual navigation

    return () => clearTimeout(timer);
  }, [navigate, duration, navigateTo]);

  const styles = {
    screen: {
      position: "fixed" as const,
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: backgroundColor,
      backgroundSize: "400% 400%",
      animation: "gradientMove 8s ease infinite",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
      flexDirection: "column" as const,
      opacity: isExiting ? 0 : 1,
      transition: "opacity 0.5s ease-out",
      pointerEvents: "none" as const,
    },
    content: {
      textAlign: "center" as const,
      display: "flex",
      flexDirection: "column" as const,
      alignItems: "center",
      gap: "20px",
      pointerEvents: "none" as const,
    },
    logo: {
      width: `${logoSize}px`,
      height: `${logoSize}px`,
      objectFit: "contain" as const,
      borderRadius: "50%",
      boxShadow: "0 0 20px rgba(180, 90, 255, 0.4)",
      animation: "scalePulse 2s infinite ease-in-out",
      background: "white",
      padding: "10px",
    },
    spinner: {
      width: "60px",
      height: "60px",
      border: "6px solid rgba(255, 255, 255, 0.2)",
      borderTop: "6px solid #7e22ce",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      backdropFilter: "blur(6px)",
      background: "rgba(255, 255, 255, 0.1)",
      boxShadow: "0 0 15px rgba(126, 34, 206, 0.3)",
      display: showSpinner ? "block" : "none",
    },
    loadingText: {
      color: "#7e22ce",
      fontSize: "17px",
      letterSpacing: "1px",
      fontWeight: 500,
      fontFamily: "Segoe UI, sans-serif",
      maxWidth: "300px",
      lineHeight: "1.5",
    },
    progressContainer: {
      width: "200px",
      height: "6px",
      background: "rgba(126, 34, 206, 0.1)",
      borderRadius: "3px",
      marginTop: "20px",
      overflow: "hidden",
    },
    progressBar: {
      height: "100%",
      width: "0%",
      background: "linear-gradient(90deg, #7e22ce, #a855f7)",
      borderRadius: "3px",
      animation: `progressAnimation ${duration}ms linear forwards`,
    },
    keyframes: `
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      @keyframes scalePulse {
        0%, 100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.07);
        }
      }
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      @keyframes gradientMove {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      @keyframes progressAnimation {
        from {
          width: 0%;
        }
        to {
          width: 100%;
        }
      }
    `,
  };

  return (
    <div style={styles.screen}>
      <style>{styles.keyframes}</style>
      <div style={styles.content}>
        <img src={logo} alt="App Logo" style={styles.logo} />
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>{loadingText}</p>
        <div style={styles.progressContainer}>
          <div style={styles.progressBar}></div>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;